import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { patienttitles } from "../../../../utils/title";
import baseUrl from "../../../../api/baseUrl";
import { UI } from "../components/appointmentScheduleUi";

function NewPatientAppointment({ formData, setformData }: any) {
  const [contactNumberStatus, setContactNumberStatus] = useState<null | "checking" | "exists" | "not_exists">(null);
  const [contactNumberFoundPatients, setContactNumberFoundPatients] = useState<any[]>([]);
  const contactNumberDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const contactNumberDropdownRef = useRef<HTMLDivElement>(null);
  const fullNameRef = useRef<HTMLInputElement>(null);

  const handlePhonenumber = (e: any) => {
    setformData({ ...formData, mobileNo: e });

    const raw = (e || "").toString().replace(/\D/g, "");
    if (contactNumberDebounceRef.current) clearTimeout(contactNumberDebounceRef.current);
    setContactNumberStatus(null);
    setContactNumberFoundPatients([]);
    if (raw.length < 5) return;

    setContactNumberStatus("checking");
    contactNumberDebounceRef.current = setTimeout(() => {
      baseUrl
        .post("client/patient/find-patients-by-phonenumber", { phonenumber: e })
        .then((res) => {
          if (!res.data.success || !Array.isArray(res.data.data)) {
            setContactNumberStatus("not_exists");
            setContactNumberFoundPatients([]);
            return;
          }
          const patients = res.data.data || [];
          setContactNumberFoundPatients(patients);
          setContactNumberStatus(patients.length > 0 ? "exists" : "not_exists");
        })
        .catch(() => {
          setContactNumberStatus("not_exists");
          setContactNumberFoundPatients([]);
        })
        .finally(() => {
          contactNumberDebounceRef.current = null;
        });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (contactNumberDebounceRef.current) clearTimeout(contactNumberDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const el = contactNumberDropdownRef.current;
      if (el && !el.contains(event.target as Node)) {
        setContactNumberStatus(null);
        setContactNumberFoundPatients([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => fullNameRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setformData((prevFormData: any) => ({ ...prevFormData, [name]: value }));
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={`${UI.label} mb-1.5`}>
          Patient name <span className="text-red-500">*</span>
        </label>
        <PatientNameFields
          formData={formData}
          handleChange={handleChange}
          fullNameRef={fullNameRef}
        />
      </div>

      <div>
        <label className={UI.label}>
          Gender <span className="text-red-500">*</span>
        </label>
        <select
          name="gender"
          required
          onChange={handleChange}
          value={formData.gender}
          className={UI.input}
        >
          <option value="">Select gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="relative" ref={contactNumberDropdownRef}>
        <label className={UI.label}>Mobile number</label>
        <div className="[&_.PhoneInput]:bg-white [&_.PhoneInput]:border [&_.PhoneInput]:border-slate-200/90 [&_.PhoneInput]:rounded-xl [&_.PhoneInput]:px-3 [&_.PhoneInput]:py-2 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:text-slate-800">
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry="LK"
            value={formData.mobileNo}
            onChange={handlePhonenumber}
          />
        </div>
        {contactNumberStatus && (
          <div className="absolute top-full left-0 right-0 z-10 mt-1 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-lg shadow-slate-900/10">
            {contactNumberStatus === "checking" && <PhoneStatusChecking />}
            {contactNumberStatus === "exists" && contactNumberFoundPatients.length > 0 && (
              <PhoneStatusExists patients={contactNumberFoundPatients} />
            )}
            {contactNumberStatus === "not_exists" && <PhoneStatusAvailable />}
          </div>
        )}
      </div>

      <div>
        <label className={UI.label}>Date of birth</label>
        <input
          type="date"
          name="dob"
          value={formData.dob}
          onChange={handleChange}
          className={UI.input}
        />
      </div>

      <div>
        <label className={UI.label}>Address</label>
        <input
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Enter address"
          className={UI.input}
        />
      </div>
    </div>
  );
}

function PatientNameFields({
  formData,
  handleChange,
  fullNameRef,
}: {
  formData: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  fullNameRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="flex gap-2 items-center w-full">
      <select
        name="title"
        required
        onChange={handleChange}
        value={formData.title}
        aria-label="Title"
        className={`${UI.inputInline} ${UI.selectTitle}`}
      >
        <option value="">Title</option>
        {patienttitles.map((item: any, i: number) => (
          <option key={i} value={item.value}>
            {item.title}
          </option>
        ))}
      </select>
      <input
        ref={fullNameRef}
        type="text"
        name="fullName"
        required
        onChange={handleChange}
        value={formData.fullName}
        placeholder="Full name"
        aria-label="Full name"
        autoComplete="name"
        className={`${UI.inputInline} ${UI.inputGrow}`}
      />
    </div>
  );
}

function PhoneStatusChecking() {
  return (
    <div className="py-2 px-3 text-xs text-slate-500 flex items-center gap-2">
      <span className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
      Checking…
    </div>
  );
}

function PhoneStatusAvailable() {
  return (
    <div className="py-2 px-3 text-xs text-emerald-600 font-medium">Available</div>
  );
}

function PhoneStatusExists({ patients }: { patients: any[] }) {
  return (
    <div className="max-h-48 overflow-y-auto py-1">
      <p className="px-3 py-1.5 text-[10px] font-semibold text-amber-600 uppercase tracking-wide">
        Already registered
      </p>
      {patients.map((patient: any, index: number) => (
        <PatientExistsRow key={patient._id || index} patient={patient} />
      ))}
    </div>
  );
}

function PatientExistsRow({ patient }: { patient: any }) {
  return (
    <div className="px-3 py-2 border-t border-slate-100 first:border-t-0">
      <p className="text-xs font-semibold text-slate-800">{patient.fullName || "—"}</p>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
        {patient.regNo != null && patient.regNo !== "" && <span>Reg: {patient.regNo}</span>}
        {patient.mobileNo && <span>{patient.mobileNo}</span>}
      </div>
    </div>
  );
}

export default NewPatientAppointment;
